import '../styles.scss';
import { useVideoLibraryActions } from 'src/store/videoLibrary/actions';
import { useChannelActions } from 'src/store/channel/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { RootState } from 'src/store/reducers/state';
import Select from 'react-select';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
  SortableContainerProps,
  SortableElementProps
} from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import SpotlightRowModal from './components/SpotlightRowModal';
import {
  Option,
  PREDEFINED_ROWS,
  ROW_TYPE_API,
  RowType,
  SPOTLIGHT_ROW_MODAL_ID,
  TYPE_META,
  cleanRowName,
  getRowType,
  isPredefinedRow,
  notPlaceholder
} from './components/spotlightRows.constants';

interface ISortableProps {
  children: React.ReactNode;
  className?: string;
}
interface ISortableItem extends SortableElementProps, ISortableProps { }
interface ISortableContainer extends SortableContainerProps, ISortableProps { }

const SORT_BY_OPTIONS = [
  { value: 'order', label: 'Sort by Order (1-20)' },
  { value: 'order_desc', label: 'Sort by Order (20-1)' },
  { value: 'title', label: 'Sort by Title (A-Z)' },
  { value: 'title_desc', label: 'Sort by Title (Z-A)' }
];

const TYPE_FILTERS: { value: 'all' | RowType; label: string }[] = [
  { value: 'all', label: 'All Row Types' },
  { value: 'predefined', label: 'Pre-defined Row' },
  { value: 'collection', label: 'Collection' },
  { value: 'category', label: 'Category' },
  { value: 'channel', label: 'Channel' }
];

const DndHandle: React.ComponentClass<ISortableProps, any> = SortableHandle(
  ({ children, className }: ISortableProps) => <div className={className || ''}>{children}</div>
);
const DndItem: React.ComponentClass<ISortableItem, any> = SortableElement(
  ({ children, className }: ISortableProps) => <div className={className || ''}>{children}</div>
);
const DndList: React.ComponentClass<ISortableContainer, any> = SortableContainer(
  ({ children, className }: ISortableProps) => <div className={className || ''}>{children}</div>
);

const SpotlightRows = () => {
  const dispatch = useDispatch();
  const channelAction = useChannelActions(dispatch);
  const libraryActions = useVideoLibraryActions(dispatch);

  const [rows, setRows] = useState<any[]>([]);
  const [moveFlag, setMoveFlag] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | RowType>('all');
  const [sortBy, setSortBy] = useState('order');
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; row?: any; key: number }>({
    mode: 'add',
    key: 0
  });
  const openModal = (mode: 'add' | 'edit', row?: any) =>
    setModal((prev) => ({ mode, row, key: prev.key + 1 }));

  const {
    spotlightChannel,
    recommendations,
    isSpotlightChannelDeleted,
    isSpotlightChannelAdded,
    isSpotlightOrderUpdated
  } = useSelector((state: RootState) => ({
    spotlightChannel: state.videoLibrary.spotlightChannel,
    recommendations: state.videoLibrary.recommendations,
    isSpotlightChannelDeleted: state.videoLibrary.isSpotlightChannelDeleted,
    isSpotlightChannelAdded: state.videoLibrary.isSpotlightChannelAdded,
    isSpotlightOrderUpdated: state.videoLibrary.isSpotlightOrderUpdated
  }));

  useEffect(() => {
    channelAction.getChannelListRequest();
  }, []);

  useEffect(() => {
    if (spotlightChannel) setRows(spotlightChannel);
  }, [spotlightChannel]);


  const activeIds: string[] = useMemo(() => rows.map((r: any) => String(r.id)), [rows]);
  const activePredefinedIds = useMemo(() => {
    const settings: any = recommendations?.spotlightSettings;
    const fromSettings = PREDEFINED_ROWS.filter((p) => settings && settings[p.key]).map((p) => p.id);
    const fromRows = rows.filter(isPredefinedRow).map((r: any) => String(r.id));
    return Array.from(new Set([...fromSettings, ...fromRows]));
  }, [rows, recommendations]);

  const collectionOptions: Option[] =
    recommendations?.collectionsHierList?.filter(notPlaceholder).map((i: any) => ({
      value: String(i.value),
      label: i.text,
      isDisabled: i.disabled
    })) || [];
  const channelOptions: Option[] =
    recommendations?.channelsHierList?.filter(notPlaceholder).map((i: any) => ({
      value: String(i.value),
      label: i.text,
      isDisabled: i.disabled
    })) || [];
  const categoryOptions: Option[] =
    recommendations?.categoriesHierList?.filter(notPlaceholder).map((i: any) => ({
      value: String(i.value),
      label: i.text,
      isDisabled: i.disabled
    })) || [];

  const isFiltered = search.trim() !== '' || typeFilter !== 'all' || sortBy !== 'order';

  const visibleRows = rows
    .map((item: any, index: number) => ({ item, index }))
    .filter(({ item }) => {
      const matchesSearch = cleanRowName(item.name).toLowerCase().includes(search.trim().toLowerCase());
      const matchesType = typeFilter === 'all' || getRowType(item) === typeFilter;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      if (sortBy === 'order_desc') return b.index - a.index;
      if (sortBy === 'title') return (a.item.name || '').localeCompare(b.item.name || '');
      if (sortBy === 'title_desc') return (b.item.name || '').localeCompare(a.item.name || '');
      return a.index - b.index;
    });


  // While dragging, stop the browser from text-selecting the rows
  const onSortStart = () => {
    document.body.style.userSelect = 'none';
    window.getSelection()?.removeAllRanges();
  };

  const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    document.body.style.userSelect = '';
    window.getSelection()?.removeAllRanges();
    if (oldIndex === newIndex) return;
    setRows(arrayMoveImmutable(rows, oldIndex, newIndex));
    setMoveFlag(true);
  };

  useEffect(() => {
    if (moveFlag) {
      libraryActions.updateSpotlightSettingOrderRequest(rows.map((r: any) => r.id));
      setMoveFlag(false);
    }
  }, [moveFlag]);

  const handleDelete = (item: any) => {
    libraryActions.deleteSpotlightChannelRequest(item.id);
  };

  const handleSave = (payload: {
    type: RowType;
    contentId: string;
    sortOrder?: string;
    contentLimit?: number;
  }) => {
    if (payload.type === 'predefined') {
      if (modal.mode === 'edit' && modal.row) {
        if (payload.contentId === String(modal.row.id)) return;
        const oldId = String(modal.row.id);
        const replaced = (spotlightChannel || []).map((r: any) =>
          String(r.id) === oldId ? payload.contentId : String(r.id)
        );
        libraryActions.deleteSpotlightChannelRequest(oldId);
        libraryActions.updateSpotlightSettingOrderRequest(replaced);
      } else {
        libraryActions.updateSpotlightSettingOrderRequest([payload.contentId, ...activeIds]);
      }
    } else {
      const body: any = {
        contentId: payload.contentId,
        rowtype: ROW_TYPE_API[payload.type],
        ...(payload.sortOrder ? { sortOrder: payload.sortOrder } : {}),
        ...(payload.contentLimit ? { contentLimit: payload.contentLimit } : {})
      };

      if (modal.mode === 'edit' && modal.row) {
        libraryActions.deleteSpotlightChannelRequest(modal.row.id);
      }
      libraryActions.addSpotlightChannelRequest(body);
    }
  };

  useEffect(() => {
    if (isSpotlightChannelDeleted || isSpotlightChannelAdded) {
      libraryActions.getSpotlightChannelRequest();
      libraryActions.getRecommendationRequest();
      libraryActions.resetVideoLibraryStateRequest();
    }
  }, [isSpotlightChannelDeleted, isSpotlightChannelAdded]);

  useEffect(() => {
    if (isSpotlightOrderUpdated) {
      libraryActions.getSpotlightChannelRequest();
      libraryActions.resetVideoLibraryStateRequest();
    }
  }, [isSpotlightOrderUpdated]);

  const gridCols = '36px 48px minmax(180px, 2fr) 150px minmax(120px, 1fr) 90px';

  return (
    <div className="spotlight-rows">
      <div className="shadow p-5 border" style={{ minHeight: 500 }}>
        <h5 className="font-semibold mb-1">Homepage Layout</h5>
        <p className="mb-4 text-gray-500">
          Manage and reorder the rows that appear on your homepage. Drag a row to change its position.
        </p>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }} className="mb-4">
          <button
            onClick={() => openModal('add')}
            type="button"
            className="btn-primary w-auto whitespace-nowrap mt-0 mb-1"
            data-bs-toggle="modal"
            data-bs-target={`#${SPOTLIGHT_ROW_MODAL_ID}`}>
            <i className="fa fa-plus mr-2"></i>Add Row
          </button>

          <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
            <i
              className="fa fa-search"
              style={{ position: 'absolute', left: 12, top: 12, color: '#9ca3af' }}></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search rows…"
              className="border rounded-lg w-full"
              style={{ height: 38, paddingLeft: 34, paddingRight: 12 }}
            />
          </div>

          <div style={{ flex: '0 0 190px' }}>
            <Select
              options={TYPE_FILTERS}
              value={TYPE_FILTERS.find((f) => f.value === typeFilter)}
              isSearchable={false}
              onChange={(val: any) => setTypeFilter(val.value)}
            />
          </div>

          <div style={{ flex: '0 0 220px' }}>
            <Select
              options={SORT_BY_OPTIONS}
              value={SORT_BY_OPTIONS.find((f) => f.value === sortBy)}
              isSearchable={false}
              onChange={(val: any) => setSortBy(val.value)}
            />
          </div>
        </div>

        <div
          className="text-sm font-semibold text-gray-600 border-b"
          style={{ display: 'grid', gridTemplateColumns: gridCols, alignItems: 'center', padding: '10px 8px' }}>
          <span></span>
          <span>#</span>
          <span>Row title</span>
          <span>Row type</span>
          <span>Source</span>
          <span style={{ textAlign: 'right' }}>Actions</span>
        </div>

        {/* Rows */}
        {rows.length ? (
          <DndList lockAxis="y" useWindowAsScrollContainer useDragHandle onSortStart={onSortStart} onSortEnd={onSortEnd}>
            {visibleRows.map(({ item, index }) => {
              const type = getRowType(item);
              const predefined = type === 'predefined';
              const cleanName = cleanRowName(item.name);
              return (
                <DndItem
                  key={`row-${item.id}`}
                  index={index}
                  disabled={isFiltered}
                  className="spotlight-row-item">
                  <div
                    className="border-b"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: gridCols,
                      alignItems: 'center',
                      padding: '12px 8px',
                      background: '#fff'
                    }}>
                    <DndHandle className={isFiltered ? 'opacity-30 select-none' : 'cursor-move select-none'}>
                      <i className="fa fa-grip-vertical text-gray-400" title={isFiltered ? 'Clear search, filter and sorting to reorder' : 'Drag to reorder'}></i>
                    </DndHandle>
                    <span className="text-gray-500">{index + 1}</span>
                    <span className="font-semibold flex items-center" style={{ minWidth: 0 }}>
                      <i
                        className={`fa ${TYPE_META[type].icon} mr-3`}
                        style={{ color: '#6d3df2', width: 18, textAlign: 'center' }}></i>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {cleanName}
                      </span>
                    </span>
                    <span className="text-gray-600">{TYPE_META[type].label}</span>
                    <span className="text-gray-600" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {predefined ? 'Standard row' : cleanName}
                    </span>
                    <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <div className="dropdown relative">
                        <button
                          className="dropdown-toggle"
                          type="button"
                          id={`action-dropdown-${item.id}`}
                          data-bs-toggle="dropdown"
                          aria-expanded="false">
                          <img src="../../assets/img/dots-vertical.svg" className="w-5 h-5" />
                        </button>
                        <ul
                          className="dropdown-menu dropdown-menu-ul dropdown-menu-end"
                          aria-labelledby={`action-dropdown-${item.id}`}>
                          <li>
                            <button
                              type="button"
                              onClick={() => openModal('edit', item)}
                              className="action-menu flex items-center"
                              data-bs-toggle="modal"
                              data-bs-target={`#${SPOTLIGHT_ROW_MODAL_ID}`}>
                              <i className="fas fa-edit w-6 text-left"></i>
                              <span>Edit</span>
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              onClick={() => handleDelete(item)}
                              className="action-menu flex items-center text-red-500">
                              <i className="fas fa-trash w-6 text-left"></i>
                              <span>Remove</span>
                            </button>
                          </li>
                        </ul>
                      </div>
                    </span>
                  </div>
                </DndItem>
              );
            })}
          </DndList>
        ) : (
          <div className="py-8 text-center text-gray-500">
            No rows yet. Select <strong>Add Row</strong> to build your homepage.
          </div>
        )}

        {rows.length > 0 && visibleRows.length === 0 && (
          <div className="py-8 text-center text-gray-500">No rows match your search.</div>
        )}

        <div className="text-sm text-gray-500 mt-3 text-right">
          Showing {visibleRows.length} of {rows.length} rows
        </div>
      </div>

      <SpotlightRowModal
        mode={modal.mode}
        editingRow={modal.row}
        openKey={modal.key}
        collectionOptions={collectionOptions}
        categoryOptions={categoryOptions}
        channelOptions={channelOptions}
        activePredefinedIds={activePredefinedIds}
        onSave={handleSave}
      />
    </div>
  );
};

export default SpotlightRows;